import { Add } from '@mui/icons-material';
import { Box, Divider, Button, MenuItem } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { Page, PageTitleWithBack, Pagination, Table, SMBSelect } from '../../../components';
import { Link } from 'react-router-dom';
import { getACHNotifications } from '../../../fetch';
import { IACHNotificationBatch } from '../../../models';
import { ACHResponsiveComponent } from './ACHResponsiveComponent';

const sortOptions = [
  { name: 'Date - Asc', value: 'Created Asc' },
  { name: 'Date - Desc', value: 'Created Desc' },
  { name: 'Batch No. - Asc', value: 'Batch Asc' },
  { name: 'Batch No. - Desc', value: 'Batch Desc' }
];

export const ACHPage = () => {
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const [selectedSort, setSelectedSort] = useState<string>('Created');
  const [sortDirection, setSortDirection] = useState<string>('Asc');
  const [displaySort, setDisplaySort] = useState('Created Asc');

  const [batchNotifications, setBatchNotifications] = useState<IACHNotificationBatch[]>([]);

  const fetchACHNotifications = async () => {
    try {
      setIsLoading(true);
      const res = await getACHNotifications({
        page: page + 1,
        sortBy: selectedSort,
        sortDirection,
        perPage
      });
      setRecordCount(res.totalRecordCount);
      setBatchNotifications(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading undefined parts, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchACHNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, sortDirection, selectedSort]);

  // These aren't really being used.
  const columns = useMemo(() => {
    return [
      {
        Header: 'NotificationId',
        accessor: 'batchPaymentNotificationId'
      },
      {
        Header: 'BatchId',
        accessor: 'orderNumbers'
      },
      {
        Header: 'Created',
        accessor: 'created'
      },
      {
        id: 'actions',
        accessor: '',
        Header: ''
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Page title="ACH">
      <PageTitleWithBack
        backText="Back to Admin"
        backUrl={'/admin'}
        title="ACH"
        showDivider={false}
        additionalHeaderContent={
          <Box display="flex" gap={1}>
            <Button
              startIcon={<Add />}
              color="primary"
              variant="contained"
              disabled={isLoading}
              component={Link}
              to="/admin/financial/ach/new-notification"
            >
              New Notification
            </Button>
            <Divider orientation="vertical" flexItem />

            <SMBSelect
              label="Sort By"
              disabled={isLoading}
              name="sortby"
              variant="outlined"
              id="sortby"
              isFullWidth={false}
              value={displaySort}
              onChange={({ target: { value } }) => {
                const selectedValue = value.split(' ');
                setSelectedSort(selectedValue[0]);
                setSortDirection(selectedValue[1]);
                setDisplaySort(value);
              }}
            >
              {sortOptions.map((option, index) => {
                return (
                  <MenuItem key={`${index}`} value={option.value}>
                    {option.name}
                  </MenuItem>
                );
              })}
            </SMBSelect>
          </Box>
        }
      />
      <Table
        mobileBreakPoint={Infinity}
        columns={columns}
        data={batchNotifications}
        isLoading={isLoading}
        hidePagination
        noResultsText="No ACH Batch Notifications Found"
        ResponsiveComponent={ACHResponsiveComponent}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </Page>
  );
};
